"use client";

import React, { useState, useEffect, useCallback } from "react";
import { Button } from "../../ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../../ui/card";
import { Label } from "../../ui/label";
import { Textarea } from "../../ui/textarea";
import { DocumentManager } from "../../planning/DocumentManager";
import { ConfirmationDialog } from "../../ui/confirmation-dialog";
import { getCompanyName } from "@/lib/textron-data-processor";
import { getFY_YEAR } from "@/utils/sharepoint-env";
import { formatNumber } from "@/utils/formatting";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../ui/table";
import {
  Calculator,
  CheckCircle,
  AlertTriangle,
  FileText,
  Users,
  DollarSign,
  Building,
  Plus,
  Minus,
  Scale,
  Gavel,
  MessageSquare,
} from "lucide-react";
import { Input } from "../../ui/input";
import { ReviewComment } from "../../shared/ReviewComment";

interface ManagerialRemunerationProps {
  onBack?: () => void;
}

interface Director {
  id: string;
  name: string;
  position: string;
  remuneration: number;
  joinedInCurrentYear?: string; // "Yes" or "No"
  dateOfJoining?: string; // ISO date string (YYYY-MM-DD)
}

interface ProfitCalculation {
  profitBeforeTax: number;
  bountiesSubsidies: number;
  items198_3: {
    profitsPremiumShares: number;
    profitsSaleForfeitedShares: number;
    profitsCapitalNature: number;
    profitsSaleImmovableProperty: number;
    fairValueChanges: number;
    unrealizedGains: number;
  };
  directorsRemuneration: number;
  sums198_5: {
    compensationDamages: number;
    lossCapitalNature: number;
    fairValueChanges: number;
  };
  profit198: number;
  maxLimit197: number;
}

interface EffectiveCapital {
  paidUpShareCapital: number;
  securitiesPremium: number;
  reservesSurplus: number;
  longTermLoans: number;
  investments: number;
  accumulatedLosses: number;
  preliminaryExpenses: number;
  effectiveCapital: number;
}

interface ComplianceResult {
  category: string;
  limit: number;
  totalActuals: number;
  compliance: string;
  remarks?: string;
  directorId?: string;
  directorName?: string;
}

const POSITION_OPTIONS = [
  "Managing Director",
  "Whole-time Director",
  "Manager",
  "Other Director"
];

const EFFECTIVE_CAPITAL_LIMITS = [
  { range: "<5cr", mdLimit: 6000000, otherLimit: 1200000 },
  { range: ">=5cr, <100cr", mdLimit: 8400000, otherLimit: 1700000 },
  { range: ">=100cr, <250cr", mdLimit: 12000000, otherLimit: 2400000 },
  { range: ">=250cr", mdLimit: 12000000, otherLimit: 2400000 },
];

export default function ManagerialRemuneration({ onBack }: ManagerialRemunerationProps) {
  const [companyName, setCompanyName] = useState<string>("");
  const [isEditMode, setIsEditMode] = useState<boolean>(false);
  const [sharePointSaveStatus, setSharePointSaveStatus] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');
  const [sharePointSaveError, setSharePointSaveError] = useState<string | null>(null);
  const [showStopEditingDialog, setShowStopEditingDialog] = useState<boolean>(false);
  const [complianceIssueTextResponse, setComplianceIssueTextResponse] = useState<any>(null);
  const [complianceIssueDocuments, setComplianceIssueDocuments] = useState<any[]>([]);
  const [inferenceQuestion, setInferenceQuestion] = useState<any>({ textResponse: null, documents: [] });
  const [reviewCommentQuestionId, setReviewCommentQuestionId] = useState<string | null>(null);

  // Generate question ID for a section
  const getSectionQuestionId = (sectionName: string): string => {
    const normalizedSection = sectionName
      .replace(/[^a-zA-Z0-9\s]/g, "")
      .replace(/\s+/g, "_")
      .toUpperCase();
    return `MANAGERIAL_REMUNERATION_${normalizedSection}`;
  };

  const [hasAdequateProfits, setHasAdequateProfits] = useState<string>("");
  const [auditPeriodEndDate, setAuditPeriodEndDate] = useState<string>(() => {
    // Default to March 31st of current year (or next year if we're past March)
    const now = new Date();
    const currentYear = now.getFullYear();
    const currentMonth = now.getMonth(); // 0-11, where 3 = April
    // If we're past March, use next year's March 31st, otherwise use this year's
    const year = currentMonth >= 3 ? currentYear + 1 : currentYear;
    return `${year}-03-31`;
  });
  const [profitCalculation, setProfitCalculation] = useState<ProfitCalculation>({
    profitBeforeTax: 0,
    bountiesSubsidies: 0,
    items198_3: {
      profitsPremiumShares: 0,
      profitsSaleForfeitedShares: 0,
      profitsCapitalNature: 0,
      profitsSaleImmovableProperty: 0,
      fairValueChanges: 0,
      unrealizedGains: 0,
    },
    directorsRemuneration: 0,
    sums198_5: {
      compensationDamages: 0,
      lossCapitalNature: 0,
      fairValueChanges: 0,
    },
    profit198: 0,
    maxLimit197: 0,
  });
  const [directors, setDirectors] = useState<Director[]>([]);
  const [effectiveCapital, setEffectiveCapital] = useState<EffectiveCapital>({
    paidUpShareCapital: 0,
    securitiesPremium: 0,
    reservesSurplus: 0,
    longTermLoans: 0,
    investments: 0,
    accumulatedLosses: 0,
    preliminaryExpenses: 0,
    effectiveCapital: 0,
  });
  const [complianceResults, setComplianceResults] = useState<ComplianceResult[]>([]);

  // Load company name
  useEffect(() => {
    const loadCompanyName = async () => {
      try {
        const name = await getCompanyName("combined");
        setCompanyName(name || "ABC Private Limited");

      } catch (error) {
        console.warn('Failed to load company name, using fallback');
        setCompanyName("");
      }
    };
    loadCompanyName();
  }, []);

  // Format data for saving to SharePoint
  const formatDataForSharePoint = useCallback(() => {
    const data: any = {
      "Section 197 Compliance Check": hasAdequateProfits || "",
      "Calculation of Net Profit u/s 198": {},
      "Calculation of Effective Capital (Schedule V)": {},
      "Audit Period End Date": auditPeriodEndDate || "",
      "Directors and Managers": {
        "columns": hasAdequateProfits === "No"
          ? ["Name", "Position", "Remuneration u/s 2(78)", "Joined in Current Year?", "Date of Joining"]
          : ["Name", "Position", "Remuneration u/s 2(78)"],
        "rows": directors.map(d => ({
          "Name": d.name || "",
          "Position": d.position || "",
          "Remuneration u/s 2(78)": d.remuneration?.toString() || "",
          ...(hasAdequateProfits === "No" && {
            "Joined in Current Year?": d.joinedInCurrentYear || "",
            "Date of Joining": d.dateOfJoining || ""
          })
        }))
      },
      "Compliance Results": {
        "columns": hasAdequateProfits === "No"
          ? ["Category", "Director Name", "Limit u/s 197", "Total at Actuals", "Compliance", "Remarks"]
          : hasAdequateProfits === "Yes"
            ? ["Category", "Limit u/s 197", "Total at Actuals", "Compliance", "Remarks"]
            : ["Category", "Limit u/s 197", "Total at Actuals", "Compliance"],
        "rows": complianceResults.map(r => ({
          "Category": r.category || "",
          ...(hasAdequateProfits === "No" && { "Director Name": r.directorName || "" }),
          "Limit u/s 197": r.limit?.toString() || "",
          "Total at Actuals": r.totalActuals?.toString() || "",
          "Compliance": r.compliance || "",
          ...((hasAdequateProfits === "No" || hasAdequateProfits === "Yes") && { "Remarks": r.remarks || "" })
        }))
      },
      "Compliance Issue": {
        "textResponse": complianceIssueTextResponse, // This will be updated in saveDataToSharePoint to check localStorage
        "documents": complianceIssueDocuments.map(doc => typeof doc === 'string' ? doc : (doc.name || doc))
      }
    };

    // Add Profit Calculation data
    if (hasAdequateProfits === "Yes") {
      data["Calculation of Net Profit u/s 198"] = {
        "Profit Before Tax": profitCalculation.profitBeforeTax?.toString() || "",
        "Add: bounties or subsidies received from the government not credited to profit & loss u/s 198(2)": profitCalculation.bountiesSubsidies?.toString() || "",
        "Less: Items u/s 198(3) as listed below which have been given credit in profit and loss account": "",
        "Profits by way of premium on shares": profitCalculation.items198_3.profitsPremiumShares?.toString() || "",
        "Profits on sale of forfeited shares by the company": profitCalculation.items198_3.profitsSaleForfeitedShares?.toString() || "",
        "Profits of capital nature including sale of undertaking(s) or part thereof": profitCalculation.items198_3.profitsCapitalNature?.toString() || "",
        "Profits from sale of immovable property or fixed assets of capital nature unless the business consists of buying and selling of such property or assets (only in excess of original cost)": profitCalculation.items198_3.profitsSaleImmovableProperty?.toString() || "",
        "Any fair value changes in carrying amount of asset or liability": profitCalculation.items198_3.fairValueChanges?.toString() || "",
        "Any amount representing unrealized gains, notional gains or revaluation of assets": profitCalculation.items198_3.unrealizedGains?.toString() || "",
        "Add: Director's remuneration deducted from the profit and loss account": profitCalculation.directorsRemuneration?.toString() || "",
        "Add: Sums that should not be deducted u/s 198(5)": "",
        "Any compensation, damages or payments made voluntarily": profitCalculation.sums198_5.compensationDamages?.toString() || "",
        "Loss of a capital nature including loss on sale of the undertaking(s) (only upto the cost of the asset)": profitCalculation.sums198_5.lossCapitalNature?.toString() || "",
        "Any fair value changes in carrying amount of asset or liability (2)": profitCalculation.sums198_5.fairValueChanges?.toString() || "",
        "Profit u/s 198": profitCalculation.profit198?.toString() || "",
        "Maximum limit u/s 197 (11%*Profit u/s 198)": profitCalculation.maxLimit197?.toString() || ""
      };
    }

    // Add Effective Capital data
    if (hasAdequateProfits === "No") {
      data["Calculation of Effective Capital (Schedule V)"] = {
        "Paid-up share capital (excluding share application money or advances against shares) [A]": effectiveCapital.paidUpShareCapital?.toString() || "",
        "Securities Premium Account [B]": effectiveCapital.securitiesPremium?.toString() || "",
        "Reserves and surplus (excluding revaluation reserve) [C]": effectiveCapital.reservesSurplus?.toString() || "",
        "Long term loans and deposits repayable after one year (excluding working capital loans, over drafts, interest due on loans unless funded, bank guarantee, etc., and other short-term arrangements) [D]": effectiveCapital.longTermLoans?.toString() || "",
        "Less: Aggregate of any investments (except in case of investment by an investment company whose principal business is acquisition of shares, stock, debentures or other securities) [E]": effectiveCapital.investments?.toString() || "",
        "Less: Accumulated losses [F]": effectiveCapital.accumulatedLosses?.toString() || "",
        "Less: Preliminary expenses not written off [G]": effectiveCapital.preliminaryExpenses?.toString() || "",
        "Effective Capital [A+B+C+D-E-F-G]": effectiveCapital.effectiveCapital?.toString() || ""
      };
    }

    return { "Managerial Remuneration": data };
  }, [
    hasAdequateProfits,
    profitCalculation,
    effectiveCapital,
    auditPeriodEndDate,
    directors,
    complianceResults,
    complianceIssueTextResponse,
    complianceIssueDocuments
  ]);

  // Load data from SharePoint
  useEffect(() => {
    const loadData = async () => {
      if (!companyName) {
        console.log('[ManagerialRemuneration] Waiting for company name...');
        return;
      }

      console.log('[ManagerialRemuneration] Loading data for company:', companyName);

      try {
        // Load directly from Execution_EBE_Responses.json
        const areaPrefix = "EBE";
        const fileName = `Execution_${areaPrefix}_Responses.json`;

        if ((window as any).sharePointAPI?.downloadClientJson) {
          const result = await (window as any).sharePointAPI.downloadClientJson(fileName);

          console.log('[ManagerialRemuneration] Load result:', {
            success: result?.success,
            hasData: !!result?.data,
            dataKeys: result?.data ? Object.keys(result.data) : []
          });

          if (result?.success && result?.data?.substantiveProcedures?.["Managerial Remuneration"]) {
            // The data comes wrapped in metadata from the SharePoint file
            const responseData = result.data.substantiveProcedures["Managerial Remuneration"];
            // Extract the actual data - it's stored in the table field
            const data = responseData.table && responseData.table.length > 0
              ? responseData.table[0]
              : (responseData.data || responseData);

            // Also load Compliance Issue from the response metadata
            if (responseData.textResponse) {
              setComplianceIssueTextResponse(responseData.textResponse);
            }
            if (responseData.documents && responseData.documents.length > 0) {
              setComplianceIssueDocuments(responseData.documents);
            }

            // Load Section 197 Compliance Check
            if (data["Section 197 Compliance Check"]) {
              setHasAdequateProfits(data["Section 197 Compliance Check"]);
            }

            // Load Profit Calculation
            if (data["Calculation of Net Profit u/s 198"]) {
              const profitData = data["Calculation of Net Profit u/s 198"];
              setProfitCalculation(prev => ({
                ...prev,
                profitBeforeTax: Number(profitData["Profit Before Tax"] || 0),
                bountiesSubsidies: Number(profitData["Add: bounties or subsidies received from the government not credited to profit & loss u/s 198(2)"] || 0),
                items198_3: {
                  profitsPremiumShares: Number(profitData["Profits by way of premium on shares"] || 0),
                  profitsSaleForfeitedShares: Number(profitData["Profits on sale of forfeited shares by the company"] || 0),
                  profitsCapitalNature: Number(profitData["Profits of capital nature including sale of undertaking(s) or part thereof"] || 0),
                  profitsSaleImmovableProperty: Number(profitData["Profits from sale of immovable property or fixed assets of capital nature unless the business consists of buying and selling of such property or assets (only in excess of original cost)"] || 0),
                  fairValueChanges: Number(profitData["Any fair value changes in carrying amount of asset or liability"] || 0),
                  unrealizedGains: Number(profitData["Any amount representing unrealized gains, notional gains or revaluation of assets"] || 0),
                },
                directorsRemuneration: Number(profitData["Add: Director's remuneration deducted from the profit and loss account"] || 0),
                sums198_5: {
                  compensationDamages: Number(profitData["Any compensation, damages or payments made voluntarily"] || 0),
                  lossCapitalNature: Number(profitData["Loss of a capital nature including loss on sale of the undertaking(s) (only upto the cost of the asset)"] || 0),
                  fairValueChanges: Number(profitData["Any fair value changes in carrying amount of asset or liability (2)"] || 0),
                },
              }));
            }

            // Load Effective Capital
            if (data["Calculation of Effective Capital (Schedule V)"]) {
              const capitalData = data["Calculation of Effective Capital (Schedule V)"];
              setEffectiveCapital(prev => ({
                ...prev,
                paidUpShareCapital: Number(capitalData["Paid-up share capital (excluding share application money or advances against shares) [A]"] || 0),
                securitiesPremium: Number(capitalData["Securities Premium Account [B]"] || 0),
                reservesSurplus: Number(capitalData["Reserves and surplus (excluding revaluation reserve) [C]"] || 0),
                longTermLoans: Number(capitalData["Long term loans and deposits repayable after one year (excluding working capital loans, over drafts, interest due on loans unless funded, bank guarantee, etc., and other short-term arrangements) [D]"] || 0),
                investments: Number(capitalData["Less: Aggregate of any investments (except in case of investment by an investment company whose principal business is acquisition of shares, stock, debentures or other securities) [E]"] || 0),
                accumulatedLosses: Number(capitalData["Less: Accumulated losses [F]"] || 0),
                preliminaryExpenses: Number(capitalData["Less: Preliminary expenses not written off [G]"] || 0),
              }));
            }

            // Load Audit Period End Date
            if (data["Audit Period End Date"]) {
              setAuditPeriodEndDate(data["Audit Period End Date"]);
            }

            // Load Directors first
            let loadedDirectors: Director[] = [];
            if (data["Directors and Managers"] && data["Directors and Managers"].rows) {
              loadedDirectors = data["Directors and Managers"].rows.map((row: any, index: number) => ({
                id: `director_${Date.now()}_${index}`,
                name: row["Name"] || "",
                position: row["Position"] || "",
                remuneration: Number(row["Remuneration u/s 2(78)"] || 0),
                joinedInCurrentYear: row["Joined in Current Year?"] || undefined,
                dateOfJoining: row["Date of Joining"] || undefined,
              }));
              setDirectors(loadedDirectors);
            }

            // Load Compliance Results - use loadedDirectors for matching since state hasn't updated yet
            if (data["Compliance Results"] && data["Compliance Results"].rows) {
              // Match Compliance Results to directors by name to get the correct directorId
              const loadedResults: ComplianceResult[] = data["Compliance Results"].rows.map((row: any, index: number) => {
                const directorName = row["Director Name"] || "";

                // Try to find matching director by name to get the correct directorId
                // Use loadedDirectors since state hasn't updated yet
                let matchedDirectorId: string | undefined = undefined;
                if (directorName && loadedDirectors.length > 0) {
                  const matchedDirector = loadedDirectors.find(d => d.name === directorName);
                  if (matchedDirector) {
                    matchedDirectorId = matchedDirector.id;
                  }
                }

                // If no match found, use index-based ID (will be updated when calculateCompliance runs)
                const directorId = matchedDirectorId || `director_${index}`;

                return {
                  category: row["Category"] || "",
                  limit: Number(row["Limit u/s 197"] || 0),
                  totalActuals: Number(row["Total at Actuals"] || 0),
                  compliance: row["Compliance"] || "",
                  remarks: row["Remarks"] || "",
                  directorId: directorId,
                  directorName: directorName,
                };
              });

              // Store loaded results temporarily to preserve remarks
              // We'll merge them after calculateCompliance runs
              setComplianceResults(loadedResults);

              // Create a map of remarks by directorId and directorName for later merging
              const remarksMap = new Map<string, string>();
              loadedResults.forEach(result => {
                if (result.directorId && result.remarks) {
                  remarksMap.set(result.directorId, result.remarks);
                }
                // Also map by directorName as fallback
                if (result.directorName && result.remarks) {
                  remarksMap.set(`name_${result.directorName}`, result.remarks);
                }
                // Also map by category for "Yes" case
                if (result.category && result.remarks) {
                  remarksMap.set(`category_${result.category}`, result.remarks);
                }
              });

              // Store remarks map in a ref or state for later use
              // We'll use this after calculateCompliance runs
              (window as any).__managerialRemunerationRemarksMap = remarksMap;
            }

            // Load Compliance Issue from the data structure
            let loadedTextResponse = null;
            let loadedDocuments: any[] = [];

            if (data["Compliance Issue"]) {
              loadedTextResponse = data["Compliance Issue"].textResponse || null;
              loadedDocuments = data["Compliance Issue"].documents || [];
            } else if (responseData.textResponse || (responseData.documents && responseData.documents.length > 0)) {
              // Fallback: load from response metadata if not in data structure
              if (responseData.textResponse) {
                loadedTextResponse = responseData.textResponse;
              }
              if (responseData.documents && responseData.documents.length > 0) {
                loadedDocuments = responseData.documents;
              }
            }

            // Set state
            if (loadedTextResponse) {
              setComplianceIssueTextResponse(loadedTextResponse);

              // Save to localStorage so DocumentManager can pick it up
              const htmlContent = loadedTextResponse.html || loadedTextResponse.plainText || '';
              if (htmlContent) {
                localStorage.setItem('textbox_ManRem', htmlContent);
                console.log('[ManagerialRemuneration] Saved Compliance Issue textResponse to localStorage, length:', htmlContent.length);
              }
            }

            if (loadedDocuments.length > 0) {
              // Convert string array to DocumentItem format if needed
              const formattedDocuments = loadedDocuments.map((doc: any) => {
                if (typeof doc === 'string') {
                  return {
                    id: `doc_${doc}`,
                    type: 'linked' as const,
                    name: doc
                  };
                }
                return {
                  id: doc.id || `doc_${doc.name || doc}`,
                  type: doc.type || 'linked',
                  name: doc.name || doc,
                  reference: doc.reference,
                  url: doc.url
                };
              });
              setComplianceIssueDocuments(formattedDocuments);
            }
          } else {
            console.log('[ManagerialRemuneration] No data found in SharePoint (this is normal for first-time use)');
          }
        } else {
          console.warn('[ManagerialRemuneration] sharePointAPI.downloadClientJson is not available');
        }
      } catch (error) {
        console.error('[ManagerialRemuneration] Failed to load data from SharePoint:', error);
      }
    };

    loadData();
  }, [companyName]);

  // Save data to SharePoint
  const saveDataToSharePoint = useCallback(async () => {
    if (!companyName) return;

    try {
      const areaPrefix = "EBE";
      const fileName = `Execution_${areaPrefix}_Responses.json`;

      // Load existing file first
      let existingData: any = {};
      try {
        const downloadResult = await (window as any).sharePointAPI?.downloadClientJson?.(fileName);
        if (downloadResult?.success && downloadResult?.data) {
          existingData = downloadResult.data;
        }
      } catch (error) {
        console.warn(`Failed to download existing ${fileName}, starting from empty`, error);
      }

      if (!existingData || typeof existingData !== "object" || Array.isArray(existingData)) {
        existingData = {};
      }

      if (!existingData.substantiveProcedures) {
        existingData.substantiveProcedures = {};
      }

      const dataToSave = formatDataForSharePoint();

      // Check localStorage for Compliance Issue textbox content (highest priority)
      const complianceIssueTextboxContent = localStorage.getItem('textbox_ManRem');
      const hasComplianceIssueTextboxContent = complianceIssueTextboxContent && complianceIssueTextboxContent.trim().length > 0;

      // Build Compliance Issue textResponse - localStorage takes priority
      let complianceIssueTextResponseData = null;
      if (hasComplianceIssueTextboxContent) {
        complianceIssueTextResponseData = {
          html: complianceIssueTextboxContent,
          plainText: complianceIssueTextboxContent.replace(/<[^>]*>/g, '')
        };
      } else if (complianceIssueTextResponse) {
        if (complianceIssueTextResponse.html || complianceIssueTextResponse.plainText) {
          complianceIssueTextResponseData = {
            html: complianceIssueTextResponse.html || complianceIssueTextResponse.plainText || '',
            plainText: complianceIssueTextResponse.plainText || (complianceIssueTextResponse.html ? complianceIssueTextResponse.html.replace(/<[^>]*>/g, '') : '')
          };
        }
      }

      // Build Compliance Issue documents array (excluding textbox type)
      const complianceIssueFileDocuments = complianceIssueDocuments
        ? complianceIssueDocuments.filter((doc: any) => typeof doc === 'string' || doc.type !== 'textbox').map((doc: any) => {
          if (typeof doc === 'string') {
            return doc;
          }
          return doc.name || doc;
        })
        : [];

      // Save each key from dataToSave
      for (const [key, value] of Object.entries(dataToSave)) {
        if (key === "Managerial Remuneration") {
          existingData.substantiveProcedures[key] = {
            selectedOption: "",
            textResponse: complianceIssueTextResponseData,
            documents: complianceIssueFileDocuments,
            table: [value],
            lastUpdated: new Date().toISOString(),
            companyName,
          };
        } else {
          existingData.substantiveProcedures[key] = {
            selectedOption: "",
            textResponse: null,
            documents: [],
            table: [value],
            lastUpdated: new Date().toISOString(),
            companyName,
          };
        }
      }

      // Save inference question - query db.json for section field like CustomProcedure
      const inferenceId = "MANAGERIAL-REMUNERATION-INFERENCE";
      const sectionToFind = `Execution_EBE_${inferenceId}`;
      let inferenceDocumentNames: string[] = [];
      try {
        if ((window as any).sharePointAPI?.loadDocumentListBySection) {
          const dbResult = await (window as any).sharePointAPI.loadDocumentListBySection(sectionToFind);
          if (dbResult?.success && dbResult?.data && Array.isArray(dbResult.data)) {
            inferenceDocumentNames = dbResult.data
              .map((file: any) => file.name || '')
              .filter((name: string) => name && name.trim().length > 0);
          }
        }
      } catch (error) {
        console.warn(`Failed to query db.json for section ${sectionToFind}:`, error);
      }

      const inferenceTextboxFromLocalStorage = localStorage.getItem(`textbox_${inferenceId}`);
      const hasInferenceTextboxContent = inferenceTextboxFromLocalStorage && inferenceTextboxFromLocalStorage.trim().length > 0;
      
      let inferenceTextResponseData = null;
      if (hasInferenceTextboxContent) {
        inferenceTextResponseData = {
          html: inferenceTextboxFromLocalStorage,
          plainText: inferenceTextboxFromLocalStorage.replace(/<[^>]*>/g, '')
        };
      } else if (inferenceQuestion.textResponse) {
        inferenceTextResponseData = inferenceQuestion.textResponse;
      }

      existingData.substantiveProcedures[inferenceId] = {
        selectedOption: "",
        textResponse: inferenceTextResponseData,
        documents: inferenceDocumentNames,
        table: [],
        lastUpdated: new Date().toISOString(),
        companyName,
      };

      // Upload to SharePoint
      const jsonString = JSON.stringify(existingData, null, 2);
      const base64Content = btoa(unescape(encodeURIComponent(jsonString)));

      // Get FY_YEAR from database dynamically
      const fyYear = getFY_YEAR();
      if (!fyYear) {
        throw new Error("FY_YEAR is not configured. Please ensure you have selected an engagement.");
      }

      const uploadResult = await (window as any).sharePointAPI?.uploadFile?.({
        fileContent: base64Content,
        fileName,
        referenceValue: `Execution ${areaPrefix} Substantive Responses`,
        folderName: "juggernaut",
        fyYear: fyYear,
        sectionList: [`Execution_${areaPrefix}_Responses`],
      });

      if (!uploadResult || !uploadResult.success) {
        throw new Error(uploadResult?.error || "Failed to save responses");
      }
    } catch (error) {
      console.error('Failed to save data to SharePoint:', error);
      throw error;
    }
  }, [companyName, formatDataForSharePoint, complianceIssueTextResponse, complianceIssueDocuments, inferenceQuestion]);

  const updateRemarks = (directorId: string | undefined, category: string | undefined, remarks: string) => {
    setComplianceResults(prev =>
      prev.map((result) => {
        if (directorId && result.directorId === directorId) {
          return { ...result, remarks };
        } else if (category && !directorId && result.category === category) {
          return { ...result, remarks };
        }
        return result;
      })
    );
  };

  const addDirector = () => {
    const newDirector: Director = {
      id: `director_${Date.now()}`,
      name: "",
      position: "",
      remuneration: 0,
      joinedInCurrentYear: undefined,
      dateOfJoining: undefined,
    };
    setDirectors(prev => [...prev, newDirector]);
  };

  const removeDirector = (id: string) => {
    setDirectors(prev => prev.filter(director => director.id !== id));
  };

  const updateDirector = (id: string, field: keyof Director, value: string | number) => {
    setDirectors(prev =>
      prev.map(director =>
        director.id === id ? { ...director, [field]: value } : director
      )
    );
  };

  const calculateProfit198 = () => {
    // Calculate total for items198_3 (sum of all sub-items)
    const totalItems198_3 =
      profitCalculation.items198_3.profitsPremiumShares +
      profitCalculation.items198_3.profitsSaleForfeitedShares +
      profitCalculation.items198_3.profitsCapitalNature +
      profitCalculation.items198_3.profitsSaleImmovableProperty +
      profitCalculation.items198_3.fairValueChanges +
      profitCalculation.items198_3.unrealizedGains;

    // Calculate total for sums198_5 (sum of all sub-items)
    const totalSums198_5 =
      profitCalculation.sums198_5.compensationDamages +
      profitCalculation.sums198_5.lossCapitalNature +
      profitCalculation.sums198_5.fairValueChanges;

    const profit198 = profitCalculation.profitBeforeTax +
      profitCalculation.bountiesSubsidies -
      totalItems198_3 +
      profitCalculation.directorsRemuneration +
      totalSums198_5;

    const maxLimit197 = profit198 * 0.11; // 11% of profit u/s 198

    setProfitCalculation(prev => ({
      ...prev,
      profit198,
      maxLimit197,
    }));
  };

  const calculateEffectiveCapital = () => {
    setEffectiveCapital(prev => {
      const calculated =
        prev.paidUpShareCapital +
        prev.securitiesPremium +
        prev.reservesSurplus +
        prev.longTermLoans -
        prev.investments -
        prev.accumulatedLosses -
        prev.preliminaryExpenses;
      return { ...prev, effectiveCapital: calculated };
    });
  };

  // Calculate proportional limit based on date of joining
  const calculateProportionalLimit = (fullLimit: number, dateOfJoining: string | undefined, auditPeriodEnd: string): number => {
    if (!dateOfJoining) {
      return fullLimit; // No date of joining means full year
    }

    try {
      // Parse dates and set to midnight to ensure accurate day calculation
      const joinDate = new Date(dateOfJoining + 'T00:00:00');
      const endDate = new Date(auditPeriodEnd + 'T00:00:00');

      // Calculate days from joining date to audit period end date (inclusive of both dates)
      const timeDiff = endDate.getTime() - joinDate.getTime();
      const daysDiff = Math.floor(timeDiff / (1000 * 60 * 60 * 24)) + 1; // Add 1 to include both dates

      // If join date is after end date, return 0
      if (daysDiff <= 0) {
        return 0;
      }

      // Calculate proportion (days in period / 365)
      const proportion = daysDiff / 365;

      // Return proportional limit
      return fullLimit * proportion;
    } catch (error) {
      console.error('Error calculating proportional limit:', error);
      return fullLimit; // Fallback to full limit on error
    }
  };

  const calculateCompliance = () => {
    const mdDirectors = directors.filter(d =>
      ["Managing Director", "Whole-time Director", "Manager"].includes(d.position)
    );
    const otherDirectors = directors.filter(d => d.position === "Other Director");

    if (hasAdequateProfits === "Yes") {
      // When adequate profits: check totals
      const mdTotal = mdDirectors.reduce((sum, d) => sum + d.remuneration, 0);
      const otherTotal = otherDirectors.reduce((sum, d) => sum + d.remuneration, 0);

      const mdCount = mdDirectors.length;
      let mdLimit = 0;
      let otherLimit = 0;

      // Calculate limits based on Section 197
      if (mdCount > 1) {
        mdLimit = profitCalculation.profit198 * 0.10; // 10%
      } else if (mdCount === 1) {
        mdLimit = profitCalculation.profit198 * 0.05; // 5%
      }

      if (mdCount === 0) {
        otherLimit = profitCalculation.profit198 * 0.03; // 3%
      } else {
        otherLimit = profitCalculation.profit198 * 0.01; // 1%
      }

      // Preserve existing remarks by category
      const existingRemarksByCategory = new Map<string, string>();
      complianceResults.forEach(result => {
        if (result.category && result.remarks) {
          existingRemarksByCategory.set(result.category, result.remarks);
        }
      });

      // Also check the remarks map stored during loading
      const loadedRemarksMap = (window as any).__managerialRemunerationRemarksMap as Map<string, string> | undefined;
      if (loadedRemarksMap) {
        loadedRemarksMap.forEach((remarks, key) => {
          if (key.startsWith('category_')) {
            const category = key.replace('category_', '');
            existingRemarksByCategory.set(category, remarks);
          }
        });
      }

      setComplianceResults([
        {
          category: "Managing director, whole-time director and manager",
          limit: mdLimit,
          totalActuals: mdTotal,
          compliance: mdTotal <= mdLimit ? "Within Limit" : "Exceeds Limit",
          remarks: existingRemarksByCategory.get("Managing director, whole-time director and manager") || "",
        },
        {
          category: "Other directors",
          limit: otherLimit,
          totalActuals: otherTotal,
          compliance: otherTotal <= otherLimit ? "Within Limit" : "Exceeds Limit",
          remarks: existingRemarksByCategory.get("Other directors") || "",
        },
      ]);
    } else {
      // When no adequate profits: check each director separately
      const capital = effectiveCapital.effectiveCapital;

      let mdLimitFull = 0;
      let otherLimitFull = 0;

      // Calculate full year limits based on Schedule V
      if (capital < 50000000) {
        mdLimitFull = 6000000;
        otherLimitFull = 1200000;
      } else if (capital < 1000000000) {
        mdLimitFull = 8400000;
        otherLimitFull = 1700000;
      } else if (capital < 2500000000) {
        mdLimitFull = 12000000;
        otherLimitFull = 2400000;
      } else {
        mdLimitFull = 12000000 + (capital - 2500000000) * 0.0001;
        otherLimitFull = 2400000 + (capital - 2500000000) * 0.0001;
      }

      // Create one result per director
      const results: ComplianceResult[] = [];

      // Preserve existing remarks by creating a map
      // Check both directorId and directorName to handle loaded data
      const existingRemarks = new Map<string, string>();
      const existingRemarksByName = new Map<string, string>();
      complianceResults.forEach(result => {
        if (result.directorId && result.remarks) {
          existingRemarks.set(result.directorId, result.remarks);
        }
        if (result.directorName && result.remarks) {
          existingRemarksByName.set(result.directorName, result.remarks);
        }
      });

      // Also check the remarks map stored during loading
      const loadedRemarksMap = (window as any).__managerialRemunerationRemarksMap as Map<string, string> | undefined;
      if (loadedRemarksMap) {
        loadedRemarksMap.forEach((remarks, key) => {
          if (key.startsWith('name_')) {
            const directorName = key.replace('name_', '');
            existingRemarksByName.set(directorName, remarks);
          } else {
            existingRemarks.set(key, remarks);
          }
        });
      }

      // Add results for MD/Whole-time/Manager directors
      mdDirectors.forEach(director => {
        // Calculate limit: if joined in current year and has date, use proportional limit
        let directorLimit = mdLimitFull;
        if (director.joinedInCurrentYear === "Yes" && director.dateOfJoining) {
          directorLimit = calculateProportionalLimit(mdLimitFull, director.dateOfJoining, auditPeriodEndDate);
        }

        // Try to get remarks by directorId first, then by directorName
        const remarks = existingRemarks.get(director.id) ||
          existingRemarksByName.get(director.name || "") ||
          "";

        results.push({
          category: director.position,
          limit: directorLimit,
          totalActuals: director.remuneration,
          compliance: director.remuneration <= directorLimit ? "Within Limit" : "Exceeds Limit",
          remarks: remarks,
          directorId: director.id,
          directorName: director.name || "Unnamed",
        });
      });

      // Add results for Other directors
      otherDirectors.forEach(director => {
        // Calculate limit: if joined in current year and has date, use proportional limit
        let directorLimit = otherLimitFull;
        if (director.joinedInCurrentYear === "Yes" && director.dateOfJoining) {
          directorLimit = calculateProportionalLimit(otherLimitFull, director.dateOfJoining, auditPeriodEndDate);
        }

        // Try to get remarks by directorId first, then by directorName
        const remarks = existingRemarks.get(director.id) ||
          existingRemarksByName.get(director.name || "") ||
          "";

        results.push({
          category: director.position,
          limit: directorLimit,
          totalActuals: director.remuneration,
          compliance: director.remuneration <= directorLimit ? "Within Limit" : "Exceeds Limit",
          remarks: remarks,
          directorId: director.id,
          directorName: director.name || "Unnamed",
        });
      });

      setComplianceResults(results);
    }
  };

  useEffect(() => {
    calculateProfit198();
  }, [
    profitCalculation.profitBeforeTax,
    profitCalculation.bountiesSubsidies,
    profitCalculation.items198_3.profitsPremiumShares,
    profitCalculation.items198_3.profitsSaleForfeitedShares,
    profitCalculation.items198_3.profitsCapitalNature,
    profitCalculation.items198_3.profitsSaleImmovableProperty,
    profitCalculation.items198_3.fairValueChanges,
    profitCalculation.items198_3.unrealizedGains,
    profitCalculation.directorsRemuneration,
    profitCalculation.sums198_5.compensationDamages,
    profitCalculation.sums198_5.lossCapitalNature,
    profitCalculation.sums198_5.fairValueChanges,
  ]);

  useEffect(() => {
    calculateEffectiveCapital();
  }, [effectiveCapital.paidUpShareCapital, effectiveCapital.securitiesPremium,
  effectiveCapital.reservesSurplus, effectiveCapital.longTermLoans,
  effectiveCapital.investments, effectiveCapital.accumulatedLosses,
  effectiveCapital.preliminaryExpenses]);

  useEffect(() => {
    calculateCompliance();
  }, [directors, hasAdequateProfits, profitCalculation.profit198, effectiveCapital.effectiveCapital, auditPeriodEndDate]);


  const getComplianceColor = (compliance: string) => {
    return compliance === "Within Limit" ? "text-green-400" : "text-red-400";
  };

  const getComplianceIcon = (compliance: string) => {
    return compliance === "Within Limit" ? (
      <CheckCircle className="h-4 w-4 text-green-500" />
    ) : (
      <AlertTriangle className="h-4 w-4 text-red-500" />
    );
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-white">Managerial Remuneration</h2>
          <p className="text-gray-400">
            Section 197/198 compliance and Schedule V calculations
          </p>
        </div>
        <div className="flex items-center gap-3">
          {!isEditMode ? (
            <Button
              variant="outline"
              onClick={() => setIsEditMode(true)}
              className="border-white/10"
            >
              Edit
            </Button>
          ) : (
            <>
              <Button
                variant="outline"
                onClick={async () => {
                  setSharePointSaveStatus('saving');
                  setSharePointSaveError(null);
                  try {
                    // Trigger auto-save for DocumentManager textboxes before saving
                    // This ensures any unsaved textbox content is saved to localStorage
                    try {
                      const autoSaveManRem = (window as any)['autoSaveTextbox_ManRem'];
                      if (autoSaveManRem && typeof autoSaveManRem === 'function') {
                        autoSaveManRem();
                      }
                      // Small delay to ensure localStorage is updated
                      await new Promise(resolve => setTimeout(resolve, 100));
                    } catch (error) {
                      console.warn('[ManagerialRemuneration] Failed to trigger auto-save for DocumentManager:', error);
                    }

                    await saveDataToSharePoint();
                    setSharePointSaveStatus('saved');
                    setIsEditMode(false);
                    setTimeout(() => setSharePointSaveStatus('idle'), 2000);
                  } catch (error) {
                    setSharePointSaveStatus('error');
                    setSharePointSaveError(error instanceof Error ? error.message : 'Failed to save');
                  }
                }}
                disabled={sharePointSaveStatus === 'saving'}
                className="border-white/10"
              >
                {sharePointSaveStatus === 'saving' ? 'Saving...' : 'Save'}
              </Button>
              <Button
                variant="outline"
                onClick={() => setShowStopEditingDialog(true)}
                className="border-white/10"
              >
                Stop Editing
              </Button>
            </>
          )}
          {sharePointSaveStatus === 'saved' && (
            <span className="text-sm text-green-400">Saved!</span>
          )}
          {sharePointSaveStatus === 'error' && sharePointSaveError && (
            <span className="text-sm text-red-400">{sharePointSaveError}</span>
          )}
          {onBack && (
            <Button variant="outline" onClick={onBack}>
              ← Back
            </Button>
          )}
        </div>
      </div>

      <ConfirmationDialog
        open={showStopEditingDialog}
        onOpenChange={setShowStopEditingDialog}
        title="Stop Editing?"
        description="Are you sure you want to stop editing? Any unsaved changes will be lost."
        yesText="Yes, Stop Editing"
        cancelText="Cancel"
        onYes={() => {
          setShowStopEditingDialog(false);
          setIsEditMode(false);
          // Reload data from SharePoint to discard changes
          window.location.reload();
        }}
        onNo={() => {
          setShowStopEditingDialog(false);
        }}
        onCancel={() => {
          setShowStopEditingDialog(false);
        }}
      />

      {/* Adequate Profits Question */}
      <Card className="border-white/10 bg-black/40">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-3 text-white">
              <Gavel className="h-5 w-5" />
              Section 197 Compliance Check
            </CardTitle>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => setReviewCommentQuestionId(getSectionQuestionId("Section 197 Compliance Check"))}
              className="flex-shrink-0 p-1 h-7 w-7 text-yellow-400 hover:text-yellow-300 hover:bg-white/10"
              title="Review Comment"
            >
              <MessageSquare className="h-4 w-4" />
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label className="text-white">
              Does the company have adequate profits to comply with the provisions of Section 197 of the Companies Act, 2013?
            </Label>
            <Select value={hasAdequateProfits} onValueChange={setHasAdequateProfits} disabled={!isEditMode}>
              <SelectTrigger className="disabled:opacity-50 disabled:cursor-not-allowed">
                <SelectValue placeholder="Select Yes or No" />
              </SelectTrigger>
              <SelectContent className="border-white/10 bg-black/90 text-white">
                <SelectItem value="Yes">Yes</SelectItem>
                <SelectItem value="No">No</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Profit Calculation (Section 198) */}
      {hasAdequateProfits === "Yes" && (
        <Card className="border-white/10 bg-black/40">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center gap-3 text-white">
                <Calculator className="h-5 w-5" />
                Calculation of Net Profit u/s 198
              </CardTitle>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => setReviewCommentQuestionId(getSectionQuestionId("Calculation of Net Profit u/s 198"))}
                className="flex-shrink-0 p-1 h-7 w-7 text-yellow-400 hover:text-yellow-300 hover:bg-white/10"
                title="Review Comment"
              >
                <MessageSquare className="h-4 w-4" />
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="text-white w-2/3">Particulars</TableHead>
                      <TableHead className="text-white w-1/3">Amount (₹)</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    <TableRow>
                      <TableCell className="text-white whitespace-normal break-words">Profit Before Tax</TableCell>
                      <TableCell className="text-white">
                        <Input
                          type="number"
                          value={profitCalculation.profitBeforeTax}
                          onChange={(e) => setProfitCalculation(prev => ({ ...prev, profitBeforeTax: Number(e.target.value) }))}
                          disabled={!isEditMode}
                          className="border-white/10 bg-black/40 text-white disabled:opacity-50 disabled:cursor-not-allowed"
                        />
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell className="text-white whitespace-normal break-words">Add: bounties or subsidies received from the government not credited to profit & loss u/s 198(2)</TableCell>
                      <TableCell className="text-white">
                        <Input
                          type="number"
                          value={profitCalculation.bountiesSubsidies}
                          onChange={(e) => setProfitCalculation(prev => ({ ...prev, bountiesSubsidies: Number(e.target.value) }))}
                          disabled={!isEditMode}
                          className="border-white/10 bg-black/40 text-white disabled:opacity-50 disabled:cursor-not-allowed"
                        />
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell className="text-white whitespace-normal break-words">Less: Items u/s 198(3) as listed below which have been given credit in profit and loss account</TableCell>
                      <TableCell className="text-white"></TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell className="text-white whitespace-normal break-words pl-8">Profits by way of premium on shares</TableCell>
                      <TableCell className="text-white">
                        <Input
                          type="number"
                          value={profitCalculation.items198_3.profitsPremiumShares}
                          onChange={(e) => setProfitCalculation(prev => ({
                            ...prev,
                            items198_3: { ...prev.items198_3, profitsPremiumShares: Number(e.target.value) }
                          }))}
                          disabled={!isEditMode}
                          className="border-white/10 bg-black/40 text-white disabled:opacity-50 disabled:cursor-not-allowed"
                        />
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell className="text-white whitespace-normal break-words pl-8">Profits on sale of forfeited shares by the company</TableCell>
                      <TableCell className="text-white">
                        <Input
                          type="number"
                          value={profitCalculation.items198_3.profitsSaleForfeitedShares}
                          onChange={(e) => setProfitCalculation(prev => ({
                            ...prev,
                            items198_3: { ...prev.items198_3, profitsSaleForfeitedShares: Number(e.target.value) }
                          }))}
                          disabled={!isEditMode}
                          className="border-white/10 bg-black/40 text-white disabled:opacity-50 disabled:cursor-not-allowed"
                        />
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell className="text-white whitespace-normal break-words pl-8">Profits of capital nature including sale of undertaking(s) or part thereof</TableCell>
                      <TableCell className="text-white">
                        <Input
                          type="number"
                          value={profitCalculation.items198_3.profitsCapitalNature}
                          onChange={(e) => setProfitCalculation(prev => ({
                            ...prev,
                            items198_3: { ...prev.items198_3, profitsCapitalNature: Number(e.target.value) }
                          }))}
                          disabled={!isEditMode}
                          className="border-white/10 bg-black/40 text-white disabled:opacity-50 disabled:cursor-not-allowed"
                        />
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell className="text-white whitespace-normal break-words pl-8">Profits from sale of immovable property or fixed assets of capital nature unless the business consists of buying and selling of such property or assets (only in excess of original cost)</TableCell>
                      <TableCell className="text-white">
                        <Input
                          type="number"
                          value={profitCalculation.items198_3.profitsSaleImmovableProperty}
                          onChange={(e) => setProfitCalculation(prev => ({
                            ...prev,
                            items198_3: { ...prev.items198_3, profitsSaleImmovableProperty: Number(e.target.value) }
                          }))}
                          disabled={!isEditMode}
                          className="border-white/10 bg-black/40 text-white disabled:opacity-50 disabled:cursor-not-allowed"
                        />
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell className="text-white whitespace-normal break-words pl-8">Any fair value changes in carrying amount of asset or liability</TableCell>
                      <TableCell className="text-white">
                        <Input
                          type="number"
                          value={profitCalculation.items198_3.fairValueChanges}
                          onChange={(e) => setProfitCalculation(prev => ({
                            ...prev,
                            items198_3: { ...prev.items198_3, fairValueChanges: Number(e.target.value) }
                          }))}
                          disabled={!isEditMode}
                          className="border-white/10 bg-black/40 text-white disabled:opacity-50 disabled:cursor-not-allowed"
                        />
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell className="text-white whitespace-normal break-words pl-8">Any amount representing unrealized gains, notional gains or revaluation of assets</TableCell>
                      <TableCell className="text-white">
                        <Input
                          type="number"
                          value={profitCalculation.items198_3.unrealizedGains}
                          onChange={(e) => setProfitCalculation(prev => ({
                            ...prev,
                            items198_3: { ...prev.items198_3, unrealizedGains: Number(e.target.value) }
                          }))}
                          disabled={!isEditMode}
                          className="border-white/10 bg-black/40 text-white disabled:opacity-50 disabled:cursor-not-allowed"
                        />
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell className="text-white whitespace-normal break-words">Add: Director's remuneration deducted from the profit and loss account</TableCell>
                      <TableCell className="text-white">
                        <Input
                          type="number"
                          value={profitCalculation.directorsRemuneration}
                          onChange={(e) => setProfitCalculation(prev => ({ ...prev, directorsRemuneration: Number(e.target.value) }))}
                          disabled={!isEditMode}
                          className="border-white/10 bg-black/40 text-white disabled:opacity-50 disabled:cursor-not-allowed"
                        />
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell className="text-white whitespace-normal break-words">Add: Sums that should not be deducted u/s 198(5)</TableCell>
                      <TableCell className="text-white"></TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell className="text-white whitespace-normal break-words pl-8">Any compensation, damages or payments made voluntarily</TableCell>
                      <TableCell className="text-white">
                        <Input
                          type="number"
                          value={profitCalculation.sums198_5.compensationDamages}
                          onChange={(e) => setProfitCalculation(prev => ({
                            ...prev,
                            sums198_5: { ...prev.sums198_5, compensationDamages: Number(e.target.value) }
                          }))}
                          disabled={!isEditMode}
                          className="border-white/10 bg-black/40 text-white disabled:opacity-50 disabled:cursor-not-allowed"
                        />
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell className="text-white whitespace-normal break-words pl-8">Loss of a capital nature including loss on sale of the undertaking(s) (only upto the cost of the asset)</TableCell>
                      <TableCell className="text-white">
                        <Input
                          type="number"
                          value={profitCalculation.sums198_5.lossCapitalNature}
                          onChange={(e) => setProfitCalculation(prev => ({
                            ...prev,
                            sums198_5: { ...prev.sums198_5, lossCapitalNature: Number(e.target.value) }
                          }))}
                          disabled={!isEditMode}
                          className="border-white/10 bg-black/40 text-white disabled:opacity-50 disabled:cursor-not-allowed"
                        />
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell className="text-white whitespace-normal break-words pl-8">Any fair value changes in carrying amount of asset or liability</TableCell>
                      <TableCell className="text-white">
                        <Input
                          type="number"
                          value={profitCalculation.sums198_5.fairValueChanges}
                          onChange={(e) => setProfitCalculation(prev => ({
                            ...prev,
                            sums198_5: { ...prev.sums198_5, fairValueChanges: Number(e.target.value) }
                          }))}
                          disabled={!isEditMode}
                          className="border-white/10 bg-black/40 text-white disabled:opacity-50 disabled:cursor-not-allowed"
                        />
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell className="text-white font-medium">Profit u/s 198</TableCell>
                      <TableCell className="text-white font-medium">{formatNumber(profitCalculation.profit198)}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell className="text-white font-bold whitespace-normal break-words">Maximum limit u/s 197 (11%*Profit u/s 198)</TableCell>
                      <TableCell className="text-white font-bold">{formatNumber(profitCalculation.maxLimit197)}</TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Effective Capital Calculation (Schedule V) */}
      {hasAdequateProfits === "No" && (
        <>
          <Card className="border-white/10 bg-black/40">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="flex items-center gap-3 text-white">
                  <Building className="h-5 w-5" />
                  Calculation of Effective Capital (Schedule V)
                </CardTitle>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => setReviewCommentQuestionId(getSectionQuestionId("Calculation of Effective Capital (Schedule V)"))}
                  className="flex-shrink-0 p-1 h-7 w-7 text-yellow-400 hover:text-yellow-300 hover:bg-white/10"
                  title="Review Comment"
                >
                  <MessageSquare className="h-4 w-4" />
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="text-white w-2/3">Particulars</TableHead>
                        <TableHead className="text-white w-1/3">Amount (₹)</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      <TableRow>
                        <TableCell className="text-white whitespace-normal break-words">Paid-up share capital (excluding share application money or advances against shares) [A]</TableCell>
                        <TableCell className="text-white">
                          <Input
                            type="number"
                            value={effectiveCapital.paidUpShareCapital}
                            onChange={(e) => setEffectiveCapital(prev => ({ ...prev, paidUpShareCapital: Number(e.target.value) }))}
                            disabled={!isEditMode}
                            className="border-white/10 bg-black/40 text-white disabled:opacity-50 disabled:cursor-not-allowed"
                          />
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell className="text-white whitespace-normal break-words">Securities Premium Account [B]</TableCell>
                        <TableCell className="text-white">
                          <Input
                            type="number"
                            value={effectiveCapital.securitiesPremium}
                            onChange={(e) => setEffectiveCapital(prev => ({ ...prev, securitiesPremium: Number(e.target.value) }))}
                            disabled={!isEditMode}
                            className="border-white/10 bg-black/40 text-white disabled:opacity-50 disabled:cursor-not-allowed"
                          />
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell className="text-white whitespace-normal break-words">Reserves and surplus (excluding revaluation reserve) [C]</TableCell>
                        <TableCell className="text-white">
                          <Input
                            type="number"
                            value={effectiveCapital.reservesSurplus}
                            onChange={(e) => setEffectiveCapital(prev => ({ ...prev, reservesSurplus: Number(e.target.value) }))}
                            disabled={!isEditMode}
                            className="border-white/10 bg-black/40 text-white disabled:opacity-50 disabled:cursor-not-allowed"
                          />
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell className="text-white whitespace-normal break-words">Long term loans and deposits repayable after one year (excluding working capital loans, over drafts, interest due on loans unless funded, bank guarantee, etc., and other short-term arrangements) [D]</TableCell>
                        <TableCell className="text-white">
                          <Input
                            type="number"
                            value={effectiveCapital.longTermLoans}
                            onChange={(e) => setEffectiveCapital(prev => ({ ...prev, longTermLoans: Number(e.target.value) }))}
                            disabled={!isEditMode}
                            className="border-white/10 bg-black/40 text-white disabled:opacity-50 disabled:cursor-not-allowed"
                          />
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell className="text-white whitespace-normal break-words">Less: Aggregate of any investments (except in case of investment by an investment company whose principal business is acquisition of shares, stock, debentures or other securities) [E]</TableCell>
                        <TableCell className="text-white">
                          <Input
                            type="number"
                            value={effectiveCapital.investments}
                            onChange={(e) => setEffectiveCapital(prev => ({ ...prev, investments: Number(e.target.value) }))}
                            disabled={!isEditMode}
                            className="border-white/10 bg-black/40 text-white disabled:opacity-50 disabled:cursor-not-allowed"
                          />
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell className="text-white whitespace-normal break-words">Less: Accumulated losses [F]</TableCell>
                        <TableCell className="text-white">
                          <Input
                            type="number"
                            value={effectiveCapital.accumulatedLosses}
                            onChange={(e) => setEffectiveCapital(prev => ({ ...prev, accumulatedLosses: Number(e.target.value) }))}
                            disabled={!isEditMode}
                            className="border-white/10 bg-black/40 text-white disabled:opacity-50 disabled:cursor-not-allowed"
                          />
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell className="text-white whitespace-normal break-words">Less: Preliminary expenses not written off [G]</TableCell>
                        <TableCell className="text-white">
                          <Input
                            type="number"
                            value={effectiveCapital.preliminaryExpenses}
                            onChange={(e) => setEffectiveCapital(prev => ({ ...prev, preliminaryExpenses: Number(e.target.value) }))}
                            disabled={!isEditMode}
                            className="border-white/10 bg-black/40 text-white disabled:opacity-50 disabled:cursor-not-allowed"
                          />
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell className="text-white font-medium whitespace-normal break-words">Effective Capital</TableCell>
                        <TableCell className="text-white font-medium">{formatNumber(effectiveCapital.effectiveCapital)}</TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Audit Period End Date */}
          <Card className="border-white/10 bg-black/40">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="flex items-center gap-3 text-white">
                  <FileText className="h-5 w-5" />
                  Audit Period
                </CardTitle>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => setReviewCommentQuestionId(getSectionQuestionId("Audit Period"))}
                  className="flex-shrink-0 p-1 h-7 w-7 text-yellow-400 hover:text-yellow-300 hover:bg-white/10"
                  title="Review Comment"
                >
                  <MessageSquare className="h-4 w-4" />
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                <Label className="text-white">Audit Period End Date</Label>
                <Input
                  type="date"
                  value={auditPeriodEndDate}
                  onChange={(e) => setAuditPeriodEndDate(e.target.value)}
                  disabled={!isEditMode}
                  className="w-full bg-black/40 border-white/10 text-white"
                />
                <p className="text-xs text-gray-400">
                  This date is used to calculate proportional limits for directors who joined during the year
                </p>
              </div>
            </CardContent>
          </Card>
        </>
      )}

      {/* Directors Management */}
      <Card className="border-white/10 bg-black/40">
        <CardHeader>
          <CardTitle className="flex items-center justify-between text-white">
            <div className="flex items-center gap-3">
              <Users className="h-5 w-5" />
              Directors and Managers
            </div>
            <div className="flex items-center gap-2">
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => setReviewCommentQuestionId(getSectionQuestionId("Directors and Managers"))}
                className="flex-shrink-0 p-1 h-7 w-7 text-yellow-400 hover:text-yellow-300 hover:bg-white/10"
                title="Review Comment"
              >
                <MessageSquare className="h-4 w-4" />
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={addDirector}
                disabled={!isEditMode}
                className="border-white/10 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Plus className="mr-2 h-3 w-3" />
                Add Director
              </Button>
            </div>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="rounded border border-blue-500/20 bg-blue-900/10 p-3 text-sm text-blue-200">
              <p className="font-medium">Note: 2(78) Remuneration means any money or its equivalent given or passed to any person for services rendered by him and includes perquisites as defined under the Income-tax Act, 1961 (43 of 1961)</p>
            </div>

            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="text-white">Name</TableHead>
                    <TableHead className="text-white">Position</TableHead>
                    <TableHead className="text-white">Remuneration u/s 2(78)</TableHead>
                    {hasAdequateProfits === "No" && (
                      <>
                        <TableHead className="text-white">Joined in Current Year?</TableHead>
                        <TableHead className="text-white">Date of Joining</TableHead>
                      </>
                    )}
                    <TableHead className="text-white">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {directors.map((director) => (
                    <TableRow key={director.id}>
                      <TableCell className="text-white">
                        <Input
                          type="text"
                          value={director.name}
                          onChange={(e) => updateDirector(director.id, 'name', e.target.value)}
                          className="border-white/10 bg-black/40 text-white"
                          placeholder="Enter name..."
                        />
                      </TableCell>
                      <TableCell className="text-white">
                        <Select
                          value={director.position}
                          onValueChange={(value) => updateDirector(director.id, 'position', value)}
                          disabled={!isEditMode}
                        >
                          <SelectTrigger className="disabled:opacity-50 disabled:cursor-not-allowed">
                            <SelectValue placeholder="Select position" />
                          </SelectTrigger>
                          <SelectContent className="border-white/10 bg-black/90 text-white">
                            {POSITION_OPTIONS.map((position) => (
                              <SelectItem key={position} value={position}>
                                {position}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </TableCell>
                      <TableCell className="text-white">
                        <Input
                          type="number"
                          value={director.remuneration}
                          onChange={(e) => updateDirector(director.id, 'remuneration', Number(e.target.value))}
                          className="border-white/10 bg-black/40 text-white"
                          placeholder="Enter amount..."
                        />
                      </TableCell>
                      {hasAdequateProfits === "No" && (
                        <>
                          <TableCell className="text-white">
                            <Select
                              value={director.joinedInCurrentYear || ""}
                              onValueChange={(value) => {
                                updateDirector(director.id, 'joinedInCurrentYear', value);
                                // If "No" is selected, clear the date of joining
                                if (value === "No") {
                                  updateDirector(director.id, 'dateOfJoining', "");
                                }
                              }}
                              disabled={!isEditMode}
                            >
                              <SelectTrigger className="disabled:opacity-50 disabled:cursor-not-allowed">
                                <SelectValue placeholder="Select..." />
                              </SelectTrigger>
                              <SelectContent className="border-white/10 bg-black/90 text-white">
                                <SelectItem value="Yes">Yes</SelectItem>
                                <SelectItem value="No">No</SelectItem>
                              </SelectContent>
                            </Select>
                          </TableCell>
                          <TableCell className="text-white">
                            <Input
                              type="date"
                              value={director.dateOfJoining || ""}
                              onChange={(e) => updateDirector(director.id, 'dateOfJoining', e.target.value)}
                              disabled={!isEditMode || director.joinedInCurrentYear !== "Yes"}
                              className="w-full bg-black/40 border-white/10 text-white"
                            />
                          </TableCell>
                        </>
                      )}
                      <TableCell className="text-white">
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => removeDirector(director.id)}
                          disabled={!isEditMode}
                          className="h-8 w-8 p-0 text-red-400 hover:text-red-300 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          <Minus className="h-3 w-3" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Compliance Results */}
      {complianceResults.length > 0 && (
        <Card className="border-white/10 bg-black/40">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center gap-3 text-white">
                <Scale className="h-5 w-5" />
                Compliance Results
              </CardTitle>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => setReviewCommentQuestionId(getSectionQuestionId("Compliance Results"))}
                className="flex-shrink-0 p-1 h-7 w-7 text-yellow-400 hover:text-yellow-300 hover:bg-white/10"
                title="Review Comment"
              >
                <MessageSquare className="h-4 w-4" />
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="text-white">Category</TableHead>
                    {hasAdequateProfits === "No" && <TableHead className="text-white">Director Name</TableHead>}
                    <TableHead className="text-white">Limit u/s 197</TableHead>
                    <TableHead className="text-white">Total at Actuals</TableHead>
                    <TableHead className="text-white">Compliance</TableHead>
                    {(hasAdequateProfits === "No" || hasAdequateProfits === "Yes") && <TableHead className="text-white">Remarks</TableHead>}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {complianceResults.map((result, index) => (
                    <TableRow key={result.directorId || index}>
                      <TableCell className="text-white">{result.category}</TableCell>
                      {hasAdequateProfits === "No" && (
                        <TableCell className="text-white">{result.directorName || "-"}</TableCell>
                      )}
                      <TableCell className="text-white">{formatNumber(result.limit)}</TableCell>
                      <TableCell className="text-white">{formatNumber(result.totalActuals)}</TableCell>
                      <TableCell className="text-white">
                        <div className="flex items-center gap-2">
                          {getComplianceIcon(result.compliance)}
                          <span className={getComplianceColor(result.compliance)}>
                            {result.compliance}
                          </span>
                        </div>
                      </TableCell>
                      {(hasAdequateProfits === "No" || hasAdequateProfits === "Yes") && (
                        <TableCell className="text-white">
                          <Textarea
                            value={result.remarks || ""}
                            onChange={(e) => updateRemarks(result.directorId, result.category, e.target.value)}
                            disabled={!isEditMode}
                            className="min-h-[60px] w-full border-white/10 bg-black/40 text-white placeholder:text-white/50 focus:border-[#4da3ff]/50 focus:ring-[#4da3ff]/20 disabled:opacity-50 disabled:cursor-not-allowed"
                            placeholder="Enter remarks..."
                            rows={2}
                          />
                        </TableCell>
                      )}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>

            {complianceResults.some(result => result.compliance === "Exceeds Limit") && (
              <div className="mt-4 rounded border border-red-500/20 bg-red-500/10 p-4">
                <div className="flex items-center gap-2">
                  <AlertTriangle className="h-5 w-5 text-red-500" />
                  <h4 className="font-medium text-red-300">Compliance Issue</h4>
                </div>
                <p className="mt-2 text-sm text-red-200">
                  The remuneration paid is in excess of limits specified u/s 197 of the Companies Act, 2013.
                  Document the details of the resolution taken at a general meeting for the same or our testing
                  on the compliance with other provisions as per the respective Section.
                </p>
                <div className="mt-3">
                  <DocumentManager
                    sectionKey="payroll"
                    groupKey="compliance-issue"
                    itemId="ManRem"
                    itemQuestion="Compliance Issue"
                    itemTextResponse={complianceIssueTextResponse}
                    itemDocuments={complianceIssueDocuments}
                    onTextResponseChange={(textResponse) => {
                      setComplianceIssueTextResponse(textResponse);
                    }}
                    onDocumentsChange={(documents) => {
                      setComplianceIssueDocuments(documents);
                    }}
                    disabled={!isEditMode}
                  />
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Inference and Conclusion Question */}
      <Card className="border-white/10 bg-black/40">
        <CardHeader>
          <CardTitle className="text-white">Document the inference and conclusion from the above procedure performed.</CardTitle>
        </CardHeader>
        <CardContent>
          <DocumentManager
            sectionKey="payroll"
            itemId="MANAGERIAL-REMUNERATION-INFERENCE"
            itemQuestion="Document the inference and conclusion from the above procedure performed."
            itemTextResponse={inferenceQuestion.textResponse}
            itemDocuments={inferenceQuestion.documents || []}
            onTextResponseChange={(textResponse) => setInferenceQuestion({ ...inferenceQuestion, textResponse })}
            onDocumentsChange={(documents) => setInferenceQuestion({ ...inferenceQuestion, documents })}
            disabled={!isEditMode}
          />
        </CardContent>
      </Card>

      {/* Review Comment Modal */}
      {reviewCommentQuestionId && (
        <ReviewComment
          questionId={reviewCommentQuestionId}
          questionText={(() => {
            // Map questionId back to section name for display
            const sectionMap: Record<string, string> = {
              [getSectionQuestionId("Section 197 Compliance Check")]: "Section 197 Compliance Check",
              [getSectionQuestionId("Calculation of Net Profit u/s 198")]: "Calculation of Net Profit u/s 198",
              [getSectionQuestionId("Calculation of Effective Capital (Schedule V)")]: "Calculation of Effective Capital (Schedule V)",
              [getSectionQuestionId("Audit Period")]: "Audit Period",
              [getSectionQuestionId("Directors and Managers")]: "Directors and Managers",
              [getSectionQuestionId("Compliance Results")]: "Compliance Results",
            };
            return sectionMap[reviewCommentQuestionId] || reviewCommentQuestionId;
          })()}
          isOpen={!!reviewCommentQuestionId}
          onClose={() => setReviewCommentQuestionId(null)}
        />
      )}
    </div>
  );
}
